import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { FormInputData } from '@usana/ux/universal-components/input/base';
import { UniversalButtonModule } from '@usana/ux/universal-components/button';
import { UniversalFormInputTextModule } from '@usana/ux/universal-components/input/text';
import { TranslationMapperService } from '../../../common/translation-mapper.service';
import { AssociateSearchResultEntry } from '../../associate-search-result-models';

@Component({
    selector: 'u-dc-associate-search',
    templateUrl: './associate-search.component.html',
    styleUrls: ['./associate-search.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, UniversalButtonModule, UniversalFormInputTextModule],
})
export class AssociateSearchComponent implements OnInit {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Input() searchResult: any;

    @Output() readonly associateSearchClicked = new EventEmitter<AssociateSearchArgs>();
    @Output() readonly closeClicked = new EventEmitter<void>();
    @Output() readonly backClicked = new EventEmitter<void>();

    readonly DELISTED_CLASSIFICATION = 'Delisted';

    labels: {
        title?: string;
        back?: string;
        'usana-id-number'?: string;
        or?: string;
        'first-name'?: string;
        'last-name'?: string;
        'search-button'?: string;
        'no-results'?: string;
    } = {};

    associateSearchForm = this.formBuilder.group(
        {
            id: [''],
            firstName: [''],
            lastName: [''],
        },
        {
            validators: [this.validateAssociateSearchInput],
        },
    );

    associateFirstNameData: FormInputData = {
        id: 'associate-first-name-text',
        name: 'associate-first-name-text',
        value: '',
        forcedState: undefined,
    };

    associateLastNameData: FormInputData = {
        id: 'associate-last-name-text',
        name: 'associate-last-name-text',
        value: '',
        forcedState: undefined,
    };

    associateSearchIdData: FormInputData = {
        id: 'associate-search-id-text',
        name: 'associate-search-id-text',
        value: '',
        forcedState: undefined,
    };

    previousSearchTerm: string;

    private readonly translationMap = {
        title: 'title',
        back: 'back',
        'usana-id-number': 'usana-id-number',
        or: 'or',
        'first-name': 'first-name',
        'last-name': 'last-name',
        'search-button': 'search-button',
        'no-results': 'no-results',
    };

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly translationMapperService: TranslationMapperService,
    ) {}

    ngOnInit(): void {
        this.translationMapperService
            .translateFromMap('associate-search', this.translationMap)
            .then((data) => {
                this.labels = data;
            });
    }

    onAssociateSearch(): void {
        this.previousSearchTerm = this.customerId || `${this.associateFirstName} ${this.associateLastName}`;
        this.associateSearchClicked.emit({
            customerId: this.customerId,
            firstName: this.associateFirstName,
            lastName: this.associateLastName,
        });
    }

    get customerId(): string {
        return this.associateSearchForm.controls.id.value;
    }

    get associateFirstName(): string {
        return this.associateSearchForm.controls.firstName.value;
    }

    get associateLastName(): string {
        return this.associateSearchForm.controls.lastName.value;
    }

    get searchResults(): AssociateSearchResultEntry[] {
        return this.searchResult?.data?.getAssociateSearch?.results;
    }

    onCloseClicked(): void {
        this.closeClicked.emit();
    }

    onBackClicked(): void {
        this.backClicked.emit();
    }

    validateAssociateSearchInput(form: FormGroup): ValidationErrors | null {
        const hasCustomerId = !!form.controls.id.value;
        const hasFullName = !!form.controls.firstName.value && !!form.controls.lastName.value;

        if (!hasCustomerId && !hasFullName) {
            return { noSearchCriteria: true };
        }

        return null;
    }
}

export interface AssociateSearchArgs {
    customerId: string;
    firstName: string;
    lastName: string;
}
